import { useState } from 'react';
import type { ReactElement } from 'react';

import { useUpdateProjectMutation } from 'features/master/api/master.api';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { describeFinancialEntityChange } from '../model/mortgageRules';
import MortgageConfirmDialog from './MortgageConfirmDialog';

interface Props {
  projectId: number;
  projectName: string;
  isFinancialEntity: boolean;
}

/*
 * Whether the company is a financial entity (Lead Management) or a real estate (Brokerage). It is the
 * master's own mark, independent of having sub-projects: an entity can split its products into them.
 */
const MortgageFinancialEntityCard = ({ projectId, projectName, isFinancialEntity }: Props): ReactElement => {
  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();
  const [pending, setPending] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async (): Promise<void> => {
    if (pending === null) return;
    setError(null);

    try {
      await updateProject({ id: projectId, patch: { isFinancialEntity: pending } }).unwrap();
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar la marca de entidad financiera.'));
    } finally {
      setPending(null);
    }
  };

  return (
    <section className="flex flex-col space-y-3 rounded-md border border-border p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 pr-2">
          <Label htmlFor="financial-entity-toggle" className="text-sm font-medium">
            Entidad financiera
          </Label>
          <p className="text-xs text-text-muted">
            Marca la compañía como entidad financiera que atiende a sus propios clientes (Lead Management). Sin la
            marca, se atiende como inmobiliaria.
          </p>
        </div>
        <Switch
          id="financial-entity-toggle"
          checked={isFinancialEntity}
          disabled={isSaving}
          onCheckedChange={(checked) => setPending(checked)}
        />
      </div>

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <MortgageConfirmDialog
        copy={pending === null ? null : describeFinancialEntityChange(pending, projectName)}
        isLoading={isSaving}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
    </section>
  );
};

export default MortgageFinancialEntityCard;
